import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import CircularLoading from "./CircularLoading";
import { II18nextTypes } from "common/src/types/i18n";
import MaterialTable, { MaterialTableProps, Options } from "material-table";

type CustomMaterialTableProps<T extends object> = MaterialTableProps<T> & {
    data: T[];
    isLoading?: boolean;
    title: keyof II18nextTypes;
    options?: Options<T> | undefined
};

export function CustomMaterialTable<T extends object>({
    data,
    title,
    options,
    isLoading = false,
    ...rest
}: CustomMaterialTableProps<T>) {
    const { t } = useTranslation();

    return (
        <Box position="relative" sx={{ md: { pt: 4, px: 2 } }}>
            <MaterialTable
                data={data}
                title={t(title) as any}
                options={{
                    search: true,
                    paging: true,
                    sorting: true,
                    loadingType: "linear",
                    addRowPosition: "first",
                    ...options
                }}
                localization={{
                    body: {
                        addTooltip: t("add"),
                        editTooltip: t("edit"),
                        deleteTooltip: t("delete"),
                        emptyDataSourceMessage: t("blank_message"),
                        editRow: {
                            saveTooltip: t("save"),
                            cancelTooltip: t("cancel"),
                            deleteText: t("delete_message"),
                        },
                    },
                    pagination: {
                        labelRowsSelect: t("rows"),
                        lastTooltip: t("last_page_tooltip"),
                        nextTooltip: t("next_page_tooltip"),
                        labelRowsPerPage: t("rows_per_page"),
                        firstTooltip: t("first_page_tooltip"),
                        previousTooltip: t("previous_page_tooltip"),
                        labelDisplayedRows: "{from}-{to} " + t("of") + " {count}",
                    },
                    header: {
                        actions: t("actions"),
                    },
                    toolbar: {
                        searchTooltip: t("search"),
                        searchPlaceholder: t("search"),
                    },
                }}
                {...rest}
            />
            {isLoading && (
                <Box
                    top={0}
                    left={0}
                    zIndex={100}
                    width="100%"
                    height="100%"
                    display="flex"
                    overflow="hidden"
                    position="absolute"
                    alignItems="center"
                    justifyContent="center"
                    style={{ backdropFilter: "blur(20px)" }}
                >
                    <CircularLoading />
                </Box>
            )}
        </Box>
    );
}